import { UserRole } from '@shared/enums/global.enum';
import { DropdownModel } from '@shared/models/dropdown-model.model';
import { Expose, Type } from 'class-transformer';
import { Country, Media } from '@shared-features/models';
import {
  FleetType,
  CustomerServiceType,
  OrganizationLabelType,
  ShippingType,
} from '@shared-features/enums';
import { environment } from 'src/environments/environment';
import * as CryptoJS from 'crypto-js';
import { Theme } from '@shared/models/theme';
import { UserParam } from './user-param.model';
import { ShipmentCreationTemplate } from '../../shipments/models/shipment-template.model';
import { Role } from '@features/auth/models/role.model';
import { TruckType } from '@features/fleets/models/truck-type.model';
import { UserDashboard } from '@features/users/models/user-dashboard.model';
import { CompanyLabelType } from '@shared-features/enums/shared-enums.enum';
import { VendorSource } from '@shared-features/enums/vendor-source.enum';

export class User extends DropdownModel {
  @Expose() id: number;
  @Expose() parentId: number;
  @Expose() firstName: string;
  @Expose() lastName: string;
  @Expose() email: string;
  @Expose() mobileNumber: string;
  @Expose() code: string;
  @Expose() country: Country;
  @Expose() roles: Role[];
  @Expose() status: string;
  @Expose() isVerified: boolean;
  @Expose() isEmailVerified: boolean;
  @Expose() isMobileNumberVerified: boolean;
  @Expose() createdAt: number;
  @Expose() updatedAt: number;
  @Expose() accessToken: string;
  @Expose() refreshToken: string;
  @Expose() infoToken: string;
  @Expose() dateOfBirth: Date;
  @Expose() password: string;
  @Expose() lastLogin?: number;
  @Type(() => Media)
  @Expose()
  profilePicture: Media = new Media();
  @Expose() hasSpecificFleets: boolean;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  set userRoles(role: any) {} // hack to stop overriding roles via transformer;
  @Expose() get userRoles(): UserRole[] {
    return (this.roles || []).map((role) => role.userRole as UserRole);
  }
  @Expose() title: string;
  @Expose() whiteLabel: Theme;
  @Expose() organizationLabel: OrganizationLabelType;
  @Expose() customDashboardToken: string;
  @Expose() customDashboardUrl: string;
  @Expose()
  @Type(() => UserDashboard)
  dashboardsInfo: UserDashboard[];
  @Expose() fleetType: FleetType;
  @Expose() customerType: CustomerServiceType;
  @Expose() defaultTruckType?: TruckType;

  @Expose()
  shippingType: ShippingType;

  @Expose()
  allowToLogin: boolean;

  @Expose()
  @Type(() => UserParam)
  params: UserParam[];

  @Type(() => ShipmentCreationTemplate)
  @Expose()
  creationTemplates: ShipmentCreationTemplate[];

  @Expose()
  isWalletEnabled: boolean;

  @Expose()
  customerLabel: CompanyLabelType;

  @Expose()
  vendorSource?: VendorSource;

  constructor() {
    super();
  }

  @Expose()
  public get name(): string {
    return this.firstName?.replace(/\//g, '') + ' ' + this.lastName?.replace(/\//g, ''); // Remove slashes to prevent issues in URLs
  }

  /*
The issue above is that "N/A" contains a slash (/), which breaks Angular routing because Angular treats slashes as path segment separators. So if you navigate to /fleets/N/A/123, Angular interprets it as:

/fleets/ → base

N → one segment

A → another segment
… instead of treating "N/A" as one value.
To fix this, we replace slashes with an empty string in the name getter. This way, if the name is "N/A", it will return "NA", which is safe for URLs.
  */

  get dropDownDisplayName(): string {
    return this.name;
  }
  get dropDownValue(): object {
    return this;
  }

  get decryptedPassword(): string {
    if (!this.password) {
      return '';
    }
    const bytes = CryptoJS.AES.decrypt(this.password, environment.encryptPasswordKey);
    const originalText = bytes.toString(CryptoJS.enc.Utf8);
    return originalText;
  }
}
